import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { useOrderForm } from '../contexts/OrderFormContext';
import DocumentUploader from '../components/DocumentUploader';
import OrderSummaryPanel from '../components/OrderSummaryPanel';
import PricingEstimate from '../components/PricingEstimate';
import PartyFields, { type PartyData } from '../components/PartyFields';
import AddressFields, { type AddressData } from '../components/AddressFields';
import FormInput, { FormSelect, FormCheckbox, FormTextarea } from '../../setup/components/FormInput';

type Step = 'service-info' | 'parties' | 'addresses' | 'documents' | 'review';

const STEPS: { id: Step; label: string }[] = [
  { id: 'service-info', label: 'Service Info' },
  { id: 'documents', label: 'Documents' },
  { id: 'parties', label: 'Parties' },
  { id: 'addresses', label: 'Serve Addresses' },
  { id: 'review', label: 'Review & Submit' },
];

const WRIT_TYPES = [
  { value: '', label: 'Select writ type...' },
  { value: 'sheriff-and-serve', label: 'Sheriff & Serve' },
  { value: 'sheriff-only', label: 'Sheriff Only' },
  { value: 'serve-only', label: 'Serve Only' },
  { value: 'sheriff-county-record-and-serve', label: 'Sheriff, County Record & Serve' },
];

const PROOF_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'file', label: 'File with court' },
  { value: 'notarize', label: 'Notarize' },
];

interface ProcessServingFormProps {
  brand: BrandConfig;
}

const ProcessServingForm: React.FC<ProcessServingFormProps> = ({ brand }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('service-info');
  const {
    caseNumber,
    caseTitle,
    priority,
    typeData,
    documents,
    updateCommonFields,
    updateTypeData,
    uploadDocument,
    removeDocument,
    submitOrder,
  } = useOrderForm();

  const data = typeData as {
    writType?: string;
    realWritType?: string;
    hearingDateTime?: string;
    timezone?: string;
    departmentDivision?: string;
    specialInstructions?: string;
    proofOption?: string;
    serveSameDocuments?: boolean;
    serveSameAddress?: boolean;
    alternateServiceNoAddress?: boolean;
    parties?: PartyData[];
    serveAddresses?: (AddressData & { serviceLevel: string; addressType: string })[];
  };

  const parties = data.parties ?? [{ name: '', capacity: 'individual' }];
  const addresses = data.serveAddresses ?? [
    { serviceLevel: 'standard', addressType: 'residence', street1: '', street2: '', city: '', state: 'CA', zip: '' },
  ];

  const stepIndex = STEPS.findIndex(s => s.id === currentStep);
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;

  return (
    <div className="flex gap-6">
      <div className="flex-1">
        {/* Step navigation */}
        <div className="flex border-b mb-6" style={{ borderColor: brand.colors.border }}>
          {STEPS.map((step, i) => (
            <button
              key={step.id}
              onClick={() => setCurrentStep(step.id)}
              className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
              style={{
                borderBottomColor: currentStep === step.id ? brand.colors.primary : 'transparent',
                color: currentStep === step.id ? brand.colors.link : brand.colors.textLight,
              }}
            >
              {i + 1}. {step.label}
            </button>
          ))}
        </div>

        {/* Step content */}
        {currentStep === 'service-info' && (
          <div className="space-y-6">
            <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>Case Information</h3>
              <FormInput brand={brand} label="Case Number" name="caseNumber" value={caseNumber} onChange={(e) => updateCommonFields({ caseNumber: e.target.value })} required />
              <FormInput brand={brand} label="Case Title" name="caseTitle" value={caseTitle} onChange={(e) => updateCommonFields({ caseTitle: e.target.value })} required />
            </div>
            <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>Service Details</h3>
              <FormSelect brand={brand} label="Writ Type" name="writType" value={data.writType ?? ''} onChange={(e) => updateTypeData({ writType: e.target.value } as Partial<typeof typeData>)} options={WRIT_TYPES} />
              <FormInput brand={brand} label="Hearing Date & Time" name="hearingDateTime" type="datetime-local" value={data.hearingDateTime ?? ''} onChange={(e) => updateTypeData({ hearingDateTime: e.target.value } as Partial<typeof typeData>)} />
              <FormInput brand={brand} label="Department/Division" name="departmentDivision" value={data.departmentDivision ?? ''} onChange={(e) => updateTypeData({ departmentDivision: e.target.value } as Partial<typeof typeData>)} />
              <FormSelect brand={brand} label="Proof Option" name="proofOption" value={data.proofOption ?? 'none'} onChange={(e) => updateTypeData({ proofOption: e.target.value } as Partial<typeof typeData>)} options={PROOF_OPTIONS} />
              <FormTextarea brand={brand} label="Special Instructions" name="specialInstructions" value={data.specialInstructions ?? ''} onChange={(e) => updateTypeData({ specialInstructions: e.target.value } as Partial<typeof typeData>)} rows={3} />
            </div>
            <PricingEstimate brand={brand} county="" priority={priority} />
          </div>
        )}

        {currentStep === 'parties' && (
          <div className="space-y-4">
            <FormCheckbox brand={brand} label="Serve same documents to all parties" name="serveSameDocuments" checked={data.serveSameDocuments ?? false} onChange={(e) => updateTypeData({ serveSameDocuments: e.target.checked } as Partial<typeof typeData>)} />
            <FormCheckbox brand={brand} label="Serve at same address for all parties" name="serveSameAddress" checked={data.serveSameAddress ?? false} onChange={(e) => updateTypeData({ serveSameAddress: e.target.checked } as Partial<typeof typeData>)} />
            {parties.map((party, i) => (
              <PartyFields
                key={`party-${party.name}-${party.capacity}`}
                brand={brand}
                index={i}
                value={party}
                onChange={(updated) => {
                  const newParties = [...parties];
                  newParties[i] = updated;
                  updateTypeData({ parties: newParties } as Partial<typeof typeData>);
                }}
                onRemove={() => {
                  updateTypeData({ parties: parties.filter((_, idx) => idx !== i) } as Partial<typeof typeData>);
                }}
                showRemove={parties.length > 1}
              />
            ))}
            <button
              onClick={() => updateTypeData({ parties: [...parties, { name: '', capacity: 'individual' }] } as Partial<typeof typeData>)}
              className="text-sm font-medium"
              style={{ color: brand.colors.link }}
            >
              + Add Party
            </button>
          </div>
        )}

        {currentStep === 'addresses' && (
          <div className="space-y-4">
            <FormCheckbox brand={brand} label="Alternate service (no address available)" name="alternateServiceNoAddress" checked={data.alternateServiceNoAddress ?? false} onChange={(e) => updateTypeData({ alternateServiceNoAddress: e.target.checked } as Partial<typeof typeData>)} />
            {!data.alternateServiceNoAddress && addresses.map((addr, i) => (
              <div key={`addr-${addr.street1}-${addr.city}-${addr.zip}`} className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs font-semibold" style={{ color: brand.colors.textLight }}>Address {i + 1}</p>
                  {addresses.length > 1 && (
                    <button onClick={() => updateTypeData({ serveAddresses: addresses.filter((_, idx) => idx !== i) } as Partial<typeof typeData>)} className="text-xs text-red-500">Remove</button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <FormSelect brand={brand} label="Address Type" name={`addrType-${i}`} value={addr.addressType} onChange={(e) => {
                    const newAddrs = [...addresses];
                    newAddrs[i] = { ...addr, addressType: e.target.value };
                    updateTypeData({ serveAddresses: newAddrs } as Partial<typeof typeData>);
                  }} options={[{ value: 'residence', label: 'Residence' }, { value: 'business', label: 'Business' }, { value: 'other', label: 'Other' }]} />
                  <FormSelect brand={brand} label="Service Level" name={`svcLevel-${i}`} value={addr.serviceLevel} onChange={(e) => {
                    const newAddrs = [...addresses];
                    newAddrs[i] = { ...addr, serviceLevel: e.target.value };
                    updateTypeData({ serveAddresses: newAddrs } as Partial<typeof typeData>);
                  }} options={[{ value: 'standard', label: 'Standard' }, { value: 'priority', label: 'Priority' }, { value: 'urgent', label: 'Urgent' }]} />
                </div>
                <AddressFields brand={brand} prefix={`serveAddr-${i}`} value={addr} onChange={(updated) => {
                  const newAddrs = [...addresses];
                  newAddrs[i] = { ...newAddrs[i], ...updated };
                  updateTypeData({ serveAddresses: newAddrs } as Partial<typeof typeData>);
                }} />
              </div>
            ))}
            {!data.alternateServiceNoAddress && (
              <button
                onClick={() => updateTypeData({ serveAddresses: [...addresses, { serviceLevel: 'standard', addressType: 'residence', street1: '', street2: '', city: '', state: 'CA', zip: '' }] } as Partial<typeof typeData>)}
                className="text-sm font-medium"
                style={{ color: brand.colors.link }}
              >
                + Add Address
              </button>
            )}
          </div>
        )}

        {currentStep === 'documents' && (
          <DocumentUploader brand={brand} documents={documents} onUpload={uploadDocument} onRemove={removeDocument} />
        )}

        {currentStep === 'review' && (
          <OrderSummaryPanel brand={brand} onSubmit={async () => {
            try {
              await submitOrder();
              navigate('/orders');
            } catch { /* Error displayed in panel */ }
          }} />
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center pt-4 mt-6 border-t" style={{ borderColor: brand.colors.border }}>
          <button
            onClick={() => setCurrentStep(STEPS[stepIndex - 1].id)}
            disabled={isFirst}
            className="px-6 py-2 rounded-lg text-sm font-medium border"
            style={{ borderColor: brand.colors.border, opacity: isFirst ? 0.5 : 1 }}
          >
            Previous
          </button>
          {!isLast && (
            <button
              onClick={() => setCurrentStep(STEPS[stepIndex + 1].id)}
              className="px-6 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              Next
            </button>
          )}
        </div>
      </div>

      <div className="w-80 flex-shrink-0 hidden lg:block">
        <OrderSummaryPanel brand={brand} onSubmit={async () => {
          try {
            await submitOrder();
            navigate('/orders');
          } catch { /* Error displayed in panel */ }
        }} />
      </div>
    </div>
  );
};

export default ProcessServingForm;
